import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Admin-only imports - ISOLATED from main app
import { AdminLogin } from './pages/AdminLogin';
import { AdminGoogleCallback } from './pages/AdminGoogleCallback';
import { ASPTab } from './components/admin/ASPTab';
import { EnhancedAdminUserManagement } from './pages/EnhancedAdminUserManagement';
import { LLMUsageAnalytics } from './pages/LLMUsageAnalytics';
import { LLMProfiles } from './pages/LLMProfiles';
import { AdminTierManagement } from './pages/AdminTierManagement';
import { AdminSubscriptionAnalytics } from './pages/AdminSubscriptionAnalytics';
import { EngagementAnalytics } from './pages/EngagementAnalytics';
import PlatformAnalyticsDashboard from './pages/admin/analytics/AnalyticsLanding';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminProductUpdates } from './pages/admin/productUpdates/AdminProductUpdates';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import { AdminLayout } from './components/AdminLayout';
import LandingPage from './pages/LandingPage';
import { AdminLottieColorPreview } from './pages/AdminLottieColorPreview';
import { AdminSystemAlerts } from './pages/AdminSystemAlerts';
import { AdminSupportInbox } from './pages/AdminSupportInbox';
import { AdminSupportTicket } from './pages/AdminSupportTicket';
import { AdminCampaigns } from './pages/AdminCampaigns';
import { AdminCampaignEditor } from './pages/AdminCampaignEditor';
// Partners (Wave 8 partner program — codes platform item C8)
import { AdminPartners } from './pages/admin/partners/AdminPartners';
import { AdminPartnerDetail } from './pages/admin/partners/AdminPartnerDetail';
import { AdminPartnerStatement } from './pages/admin/partners/AdminPartnerStatement';
// The one Codes area (Codes PR-8): Overview, All codes (+ batches), Offers, Store inventory (+ Store
// setup), Issue codes (Summer50 & one-off, Coach Cedar, a partner), API, Activity — and the redirects
// that keep the old addresses (/admin/growth, /admin/store-access, /admin/codes/house, /admin/codes/batches) working.
import { codesRouteElements } from './pages/admin/codes/codesRoutes';
import { AdminSurveyResults } from './pages/AdminSurveyResults';
import { AdminEmailManagement } from './pages/AdminEmailManagement';
import { DatabaseInspector } from './components/admin/DatabaseInspector';
import { AdminBlogCMS } from './pages/AdminBlogCMS';
import { AdminBlogEditor } from './pages/AdminBlogEditor';
import { AdminModelIntelligence } from './pages/AdminModelIntelligence';
import { AdminDebriefsAudit } from './pages/AdminDebriefsAudit';
import { AdminModelLibrary } from './pages/AdminModelLibrary';
import { AdminProductManagement } from './pages/AdminProductManagement';
import { AdminRoadmap } from './pages/AdminRoadmap';
import { AdminCICDPipeline } from './pages/AdminCICDPipeline';
import BranchDrift from './pages/admin/cicd/BranchDrift';
import AdminAppVersionConfig from './pages/AdminAppVersionConfig';
import SettingsGeneral from './pages/admin/settings/SettingsGeneral';
import SettingsNotifications from './pages/admin/settings/SettingsNotifications';
import SettingsSecurity from './pages/admin/settings/SettingsSecurity';
import AuditLog from './pages/admin/settings/AuditLog';
import MyPreferences from './pages/admin/me/MyPreferences';
import AdminAlerts from './pages/admin/alerts/AdminAlerts';
import AdminPlanHealthDashboard from './pages/AdminPlanHealthDashboard';
import { ResearcherKnowledgePortal } from './pages/admin/ResearcherKnowledgePortal';
import { MarketingDashboard } from './pages/admin/marketing/MarketingDashboard';
import { MarketingSettings } from './pages/admin/marketing/MarketingSettings';
import { BrandKitPage } from './pages/admin/marketing/BrandKitPage';
import { PipelinePage } from './pages/admin/marketing/PipelinePage';
import { ListeningPage } from './pages/admin/marketing/ListeningPage';
import { EngagementInbox } from './pages/admin/marketing/EngagementInbox';
import { AudiencePage } from './pages/admin/marketing/AudiencePage';
import { LandingPagesPage } from './pages/admin/marketing/LandingPagesPage';
import { CreativeStudioPage } from './pages/admin/marketing/CreativeStudioPage';
import { BudgetConsole } from './pages/admin/marketing/BudgetConsole';
import { MarketingAnalyticsPage } from './pages/admin/marketing/MarketingAnalyticsPage';
import { MediaRelationsPage } from './pages/admin/marketing/MediaRelationsPage';
import { AdminTeamList } from './pages/admin/team/AdminTeamList';
import { AcceptInvitePage } from './pages/admin/team/AcceptInvitePage';

/**
 * 🔐 ADMIN APP - COMPLETELY ISOLATED
 * 
 * This app is ONLY used for Firebase hosting (coach-cedar-beta.web.app)
 * and admin paths. It contains NO main app code.
 * 
 * Updated: Jan 2025 - Latest deployment with email fixes
 * Force deployment test - removed test dependencies
 */
function AdminApp() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        {/* Public — invite_token is the auth credential, no admin login required */}
        <Route path="/admin/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/admin/auth/google/callback" element={<AdminGoogleCallback />} />
        <Route path="/admin/*" element={
          <AdminProtectedRoute>
            <Routes>
              {/* All admin pages wrapped with AdminLayout for consistent navigation */}
              <Route path="dashboard" element={
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              } />
              <Route path="users" element={
                <AdminLayout>
                  <EnhancedAdminUserManagement />
                </AdminLayout>
              } />
              <Route path="team" element={
                <AdminLayout>
                  <AdminTeamList />
                </AdminLayout>
              } />
              <Route path="llm-usage" element={
                <AdminLayout>
                  <LLMUsageAnalytics />
                </AdminLayout>
              } />
              <Route path="llm-profiles" element={
                <AdminLayout>
                  <LLMProfiles />
                </AdminLayout>
              } />
              <Route path="analytics" element={
                <AdminLayout>
                  <PlatformAnalyticsDashboard />
                </AdminLayout>
              } />
              <Route path="tiers" element={
                <AdminLayout>
                  <AdminTierManagement />
                </AdminLayout>
              } />
              <Route path="subscription-analytics" element={
                <AdminLayout>
                  <AdminSubscriptionAnalytics />
                </AdminLayout>
              } />
              
              <Route path="model-intelligence" element={
                <AdminLayout>
                  <AdminModelIntelligence />
                </AdminLayout>
              } />
              <Route path="debriefs-audit" element={
                <AdminLayout>
                  <AdminDebriefsAudit />
                </AdminLayout>
              } />
              <Route path="model-library" element={
                <AdminLayout>
                  <AdminModelLibrary />
                </AdminLayout>
              } />
              <Route path="lottie-colors" element={
                <AdminLayout>
                  <AdminLottieColorPreview />
                </AdminLayout>
              } />

              <Route path="system-alerts" element={
                <AdminLayout>
                  <AdminSystemAlerts />
                </AdminLayout>
              } />
              <Route path="plan-health" element={
                <AdminLayout>
                  <AdminPlanHealthDashboard />
                </AdminLayout>
              } />
              <Route path="support" element={
                <AdminLayout>
                  <AdminSupportInbox />
                </AdminLayout>
              } />
              <Route path="support/:ticketId" element={
                <AdminLayout>
                  <AdminSupportTicket />
                </AdminLayout>
              } />

              {/* Email Management System */}
              <Route path="emails" element={
                <AdminLayout>
                  <AdminEmailManagement />
                </AdminLayout>
              } />

              {/* Mission 35: Campaign System */}
              <Route path="campaigns" element={
                <AdminLayout>
                  <AdminCampaigns />
                </AdminLayout>
              } />
              {/* Partners (Wave 8) — the static `statements/:id` segment outranks `:partnerId` */}
              <Route path="partners" element={
                <AdminLayout>
                  <AdminPartners />
                </AdminLayout>
              } />
              <Route path="partners/statements/:statementId" element={
                <AdminLayout>
                  <AdminPartnerStatement />
                </AdminLayout>
              } />
              <Route path="partners/:partnerId" element={
                <AdminLayout>
                  <AdminPartnerDetail />
                </AdminLayout>
              } />
              {/* Codes (Codes PR-8): every page of the area, and the old addresses' redirects */}
              {codesRouteElements((page) => <AdminLayout>{page}</AdminLayout>)}
              <Route path="campaigns/new" element={
                <AdminLayout>
                  <AdminCampaignEditor />
                </AdminLayout>
              } />
              <Route path="campaigns/:id" element={
                <AdminLayout>
                  <AdminCampaignEditor />
                </AdminLayout>
              } />
              <Route path="campaigns/:id/results" element={
                <AdminLayout>
                  <AdminSurveyResults />
                </AdminLayout>
              } />

              {/* Database Inspector - Purpose-bound, scoped read-only DB access */}
              <Route path="db-inspector" element={
                <AdminLayout>
                  <DatabaseInspector />
                </AdminLayout>
              } />

              {/* CC Marketing: Blog CMS */}
              <Route path="marketing/blog" element={
                <AdminLayout>
                  <AdminBlogCMS />
                </AdminLayout>
              } />
              <Route path="marketing/blog/new" element={
                <AdminLayout>
                  <AdminBlogEditor />
                </AdminLayout>
              } />
              <Route path="marketing/blog/:id" element={
                <AdminLayout>
                  <AdminBlogEditor />
                </AdminLayout>
              } />

              {/* CI/CD Pipeline */}
              <Route path="cicd" element={
                <AdminLayout>
                  <AdminCICDPipeline />
                </AdminLayout>
              } />

              {/* CI/CD — Branch Drift (Item 1 of Stop-Sunday sprint) */}
              <Route path="cicd/drift" element={
                <AdminLayout>
                  <BranchDrift />
                </AdminLayout>
              } />

              {/* App Version Config (force-update management) */}
              <Route path="app-version" element={
                <AdminLayout>
                  <AdminAppVersionConfig />
                </AdminLayout>
              } />

              {/* My preferences — the signed-in admin's own settings (same scope, WS-F) */}
              <Route path="me" element={
                <AdminLayout>
                  <MyPreferences />
                </AdminLayout>
              } />

              {/* Alert Center — one inbox for every alert (same scope, WS-I) */}
              <Route path="alerts" element={
                <AdminLayout>
                  <AdminAlerts />
                </AdminLayout>
              } />

              {/* Settings — central portal settings (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md) */}
              <Route path="settings" element={<Navigate to="/admin/settings/general" replace />} />
              <Route path="settings/general" element={
                <AdminLayout>
                  <SettingsGeneral />
                </AdminLayout>
              } />
              <Route path="settings/notifications" element={
                <AdminLayout>
                  <SettingsNotifications />
                </AdminLayout>
              } />
              <Route path="settings/security" element={
                <AdminLayout>
                  <SettingsSecurity />
                </AdminLayout>
              } />
              <Route path="settings/audit-log" element={
                <AdminLayout>
                  <AuditLog />
                </AdminLayout>
              } />

              {/* Product Management */}
              <Route path="product" element={
                <AdminLayout>
                  <AdminProductManagement />
                </AdminLayout>
              } />

              <Route path="product-updates" element={
                <AdminLayout>
                  <AdminProductUpdates />
                </AdminLayout>
              } />

              {/* Roadmap Moderation */}
              <Route path="roadmap" element={
                <AdminLayout>
                  <AdminRoadmap />
                </AdminLayout>
              } />

              {/* Researcher Knowledge Portal */}
              <Route path="researcher-knowledge" element={
                <AdminLayout>
                  <ResearcherKnowledgePortal />
                </AdminLayout>
              } />
              {/* NK-CAT practitioner-library curation runs (formerly "System Processes" on the
                  removed /admin/comprehensive page). */}
              <Route path="research/curation-runs" element={
                <AdminLayout>
                  <div className="p-4 sm:p-6"><ASPTab /></div>
                </AdminLayout>
              } />

              {/* Marketing Terminal — Phase 1 */}
              <Route path="marketing" element={
                <AdminLayout>
                  <MarketingDashboard />
                </AdminLayout>
              } />
              <Route path="marketing/social" element={
                <AdminLayout>
                  <PipelinePage />
                </AdminLayout>
              } />
              <Route path="marketing/brand" element={
                <AdminLayout>
                  <BrandKitPage />
                </AdminLayout>
              } />
              <Route path="marketing/settings" element={
                <AdminLayout>
                  <MarketingSettings />
                </AdminLayout>
              } />
              <Route path="marketing/listening" element={
                <AdminLayout>
                  <ListeningPage />
                </AdminLayout>
              } />
              <Route path="marketing/engagement" element={
                <AdminLayout>
                  <EngagementInbox />
                </AdminLayout>
              } />
              <Route path="marketing/audience" element={
                <AdminLayout>
                  <AudiencePage />
                </AdminLayout>
              } />
              <Route path="marketing/landing-pages" element={
                <AdminLayout>
                  <LandingPagesPage />
                </AdminLayout>
              } />
              <Route path="marketing/creative" element={
                <AdminLayout>
                  <CreativeStudioPage />
                </AdminLayout>
              } />
              <Route path="marketing/analytics" element={
                <AdminLayout>
                  <MarketingAnalyticsPage />
                </AdminLayout>
              } />
              <Route path="marketing/budgets" element={
                <AdminLayout>
                  <BudgetConsole />
                </AdminLayout>
              } />
              <Route path="marketing/media" element={
                <AdminLayout>
                  <MediaRelationsPage />
                </AdminLayout>
              } />
              {/* NOTE: marketing/blog routes are still served by AdminBlogCMS/Editor below.
                  Phase 6 added blog SEO endpoints (RSS, sitemap, JSON-LD, related posts)
                  consumed by the existing blog editor + public website. */}

              {/* Legacy routes */}
              {/* The old all-in-one page (ComprehensiveAdminPortal) was removed 2026-09-29: every
                  tab was covered elsewhere or broken, except System Processes, which moved to
                  Research → Curation runs. Old bookmarks land on the dashboard. */}
              <Route path="comprehensive" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="engagement" element={<EngagementAnalytics />} />
              <Route path="" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </AdminProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default AdminApp;
